import dayjs from "dayjs";
import { FC, useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useDispatch } from "react-redux";
import {
  AccordionHeaderText,
  FormController,
  ITabs,
  MuiBox,
  MuiGrid,
  MuiSelect,
  MuiTabPanel,
  MuiTabs,
  MuiTextField,
  OptionsDataType,
} from "../../../../../../../../../../components";
import { useSiteCore } from "../../../../../../../../../../hooks/useSiteCore";
import {
  ParticipantActivityResponse,
  PrimaryClosingReasonType,
  ProgramClosureFormKey,
} from "../../../../../../../../modelsAndSchemas/Models";

import { useGetCountryListForDropdowns } from "../../../../../../../../../../api/services/client/clientApis";
import {
  useGetParticipantActivitiesList,
  useGetProgramCloserTimeToLand,
} from "../../../../../../../../../../api/services/program/programApis";
import { useGetProgramClosingReasonList } from "../../../../../../../../../../api/services/programTemplate/programTemplateApis";
import {
  DATE_FORMAT,
  EMPTY_VALUE_DASH,
  PROGRAM_CLOSURE_LANDED_SUCCESSFULLY_ID,
} from "../../../../../../../../../../helpers/constants";
import {
  isNotEmpty,
  setApiDataToDropDownOption,
} from "../../../../../../../../../../helpers/utils";
import { useNavigationSiteCore } from "../../../../../../../../../../hooks/useNavigationSiteCore";
import {
  toggleLoader,
  toggleToast,
} from "../../../../../../../../../../redux/slices/appState/appStateSlice";
import { programSiteCoreKeys } from "../../../../../../../../siteCore/SiteCoreKeys";
import { EmployeeInformation } from "./tabs/employeeInformation/EmployeeInformation";
import { GeneralInformation } from "./tabs/generalInformation/GeneralInformation";

interface ProgramClosureProps {
  pageId: string;
  programId: string;
  resetLandedValues: () => void;
  userCountryDateFormat?: string;
  programStartDate?: string;
  closingReasonTypeKey?: string;
  secondaryClosingList: OptionsDataType[];
  setSecondaryClosingList: (data: OptionsDataType[]) => void;
  currentProgramStatus: string;
  deliveryCountry?: string;
  selectedTab: number;
  isVisibleLandingInformation: boolean;
  onTabChangeCallback: (tabIndex: number) => void;
}

export const ProgramClosure: FC<ProgramClosureProps> = ({
  pageId,
  programId,
  resetLandedValues,
  userCountryDateFormat = "",
  programStartDate = "",
  closingReasonTypeKey = "",
  secondaryClosingList,
  setSecondaryClosingList,
  currentProgramStatus,
  deliveryCountry = "",
  selectedTab = 0,
  isVisibleLandingInformation,
  onTabChangeCallback,
}) => {
  const dispatch = useDispatch();

  const [primaryClosingList, setPrimaryClosingList] = useState<
    OptionsDataType[]
  >([]);
  const { fetchLabel, commonLabels, fetchDropdownListValueByKey } =
    useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const { control, watch, getValues, setValue } = useFormContext();
  const gridSize = { md: 4, sm: 6, xs: 12 };

  const {
    data: countryListData,
    isFetching: countryPending,
    isError: countryError,
  } = useGetCountryListForDropdowns();

  const {
    data: closingReasonList,
    isFetching: closingReasonPending,
    isError: closingReasonError,
  } = useGetProgramClosingReasonList();

  const {
    data: activityTimelineData,
    isFetching: isActivityTimelinePending,
    isError: isActivityTimelineError,
    refetchWithParams: getActivityTimelineData,
  } = useGetParticipantActivitiesList();

  const {
    data: programCloserTimeToLandData,
    isFetching: programCloserTimeToLandPending,
    isError: programCloserTimeToLandError,
    refetchWithParams: refetchProgramCloserTimeToLand,
  } = useGetProgramCloserTimeToLand();

  useEffect(() => {
    getActivityTimelineData({ id: programId });
  }, []);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible:
          countryPending ||
          closingReasonPending ||
          programCloserTimeToLandPending ||
          isActivityTimelinePending,
        message: commonLabels.loadingPleaseWait,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [
    countryPending,
    closingReasonPending,
    programCloserTimeToLandPending,
    isActivityTimelinePending,
  ]);

  useEffect(() => {
    if (
      countryError ||
      closingReasonError ||
      programCloserTimeToLandError ||
      isActivityTimelineError
    ) {
      dispatch(
        toggleToast({
          open: true,
          message: commonLabels.someThingWentWrongPleaseTryAgainLater,
          type: "error",
        }),
      );
    }
  }, [
    countryError,
    closingReasonError,
    programCloserTimeToLandError,
    isActivityTimelineError,
  ]);

  const getLabel = (key: string) => {
    return fetchLabel(programSiteCoreKeys.changeStatusModelPopupKey, key);
  };

  const tabIdGeneral = `${pageId}-tab1`;
  const tabIdEmployer = `${pageId}-tab2`;
  const tabs: ITabs[] = [
    {
      id: tabIdGeneral,
      label: getLabel(programSiteCoreKeys.general),
    },
    {
      id: tabIdEmployer,
      label: getLabel(programSiteCoreKeys.employerInformationTab),
    },
  ];

  const renderHeader = (key: string) => {
    return (
      <MuiBox className="closure-header">
        <AccordionHeaderText label={getLabel(key)} />
      </MuiBox>
    );
  };

  const onTabChange = (event: React.ChangeEvent<{}>, value: number) => {
    onTabChangeCallback(value);
  };

  const getPrimaryReasonsByClosingReasonId = () => {
    const primaryClosingReasonObject = closingReasonList?.find(
      (item: OptionsDataType) => item.siteCoreKey === closingReasonTypeKey,
    );
    if (isNotEmpty(primaryClosingReasonObject)) {
      return (
        primaryClosingReasonObject?.primaryClosingReason?.filter(
          (reason: PrimaryClosingReasonType) =>
            reason?.primaryClosingReasonStatus?.includes(currentProgramStatus),
        ) || []
      );
    }
    return [];
  };

  const setSecondaryClosingListInDropdown = (
    primaryClosingReasonId: string,
  ) => {
    const primaryReasonList = getPrimaryReasonsByClosingReasonId();

    const primaryClosingReasonObject = primaryReasonList.find(
      (item: OptionsDataType) => item.id === primaryClosingReasonId,
    );

    if (isNotEmpty(primaryClosingReasonObject)) {
      setSecondaryClosingList(
        fetchDropdownListValueByKey(
          programSiteCoreKeys.changeStatusModelPopupKey,
          programSiteCoreKeys.secondaryClosingReasonList,
          primaryClosingReasonObject?.secondaryClosingReason ?? [],
        ),
      );
    } else {
      setSecondaryClosingList([]);
    }
  };

  const getSitecoreKeyFromClosingReasonList = (
    primaryClosingReasonId: string,
  ) => {
    const primaryReasonList = getPrimaryReasonsByClosingReasonId();
    const primaryClosingReasonObject = primaryReasonList?.find(
      (item: OptionsDataType) => item.id === primaryClosingReasonId,
    );
    return primaryClosingReasonObject?.siteCoreKey || "";
  };

  useEffect(() => {
    const primaryClosingReason = getValues(
      ProgramClosureFormKey.primaryClosingReason,
    );

    resetLandedValues();
    setValue(ProgramClosureFormKey.secondaryClosingReason, "");
    setValue(
      ProgramClosureFormKey.selectedPrimaryClosingReasonSitecoreKey,
      getSitecoreKeyFromClosingReasonList(primaryClosingReason),
    );

    if (isNotEmpty(primaryClosingReason)) {
      setSecondaryClosingListInDropdown(primaryClosingReason);
    } else {
      setSecondaryClosingList([]);
    }
  }, [watch(ProgramClosureFormKey.primaryClosingReason)]);

  useEffect(() => {
    if (isNotEmpty(closingReasonList) && closingReasonList.length > 0) {
      const primaryReasonList = getPrimaryReasonsByClosingReasonId();
      if (isNotEmpty(primaryReasonList)) {
        setPrimaryClosingList(
          fetchDropdownListValueByKey(
            programSiteCoreKeys.changeStatusModelPopupKey,
            programSiteCoreKeys.primaryClosingReasonList,
            primaryReasonList,
          ),
        );
      }
    }
  }, [closingReasonList]);

  const resetLandingDateOnLandDateDateChange = () => {
    setValue(ProgramClosureFormKey.timeToLand, EMPTY_VALUE_DASH);
  };

  //call the api when landDate date change
  useEffect(() => {
    const landedDate = getValues(ProgramClosureFormKey.landedDate);
    if (isNotEmpty(landedDate)) {
      const date = dayjs(landedDate, userCountryDateFormat)
        .endOf("day")
        .utc(true)
        .format(DATE_FORMAT.YYYY_MM_DD_HYPHEN);

      refetchProgramCloserTimeToLand({
        programId,
        landedDate: date,
      });
    } else {
      resetLandingDateOnLandDateDateChange();
    }
  }, [watch(ProgramClosureFormKey.landedDate)]);

  useEffect(() => {
    if (isNotEmpty(programCloserTimeToLandData)) {
      setValue(
        ProgramClosureFormKey.timeToLand,
        programCloserTimeToLandData?.timeToLandInDays ?? EMPTY_VALUE_DASH,
      );
    } else {
      resetLandingDateOnLandDateDateChange();
    }
  }, [programCloserTimeToLandData]);

  useEffect(() => {
    //count the number of meeting based on activity timeline data
    if (isNotEmpty(activityTimelineData)) {
      const totalMeetings = activityTimelineData.filter(
        (item: ParticipantActivityResponse) =>
          item.activityType ===
          programSiteCoreKeys.activityTypeCareerCoachMeetingKey,
      ).length;
      setValue(
        ProgramClosureFormKey.totalNumberOfMeetings,
        totalMeetings ?? EMPTY_VALUE_DASH,
      );
    } else {
      setValue(ProgramClosureFormKey.totalNumberOfMeetings, EMPTY_VALUE_DASH);
    }
  }, [
    activityTimelineData,
    watch(ProgramClosureFormKey.totalNumberOfMeetings),
  ]);

  return (
    <MuiBox data-testid="program-closure">
      {renderHeader(programSiteCoreKeys.closureInformationHeader)}
      <MuiGrid container className="form-container change-status-form">
        <MuiGrid size={gridSize} className="form-field">
          <FormController
            control={control}
            name={ProgramClosureFormKey.primaryClosingReason}
          >
            <MuiSelect
              id={`${pageId}-${ProgramClosureFormKey.primaryClosingReason}`}
              label={getLabel(programSiteCoreKeys.primaryClosingReason)}
              placeholder={commonLabels.selectPlaceHolder}
              options={primaryClosingList}
              isMandatory
              visibleDefaultSelectOption
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>
        {isNotEmpty(secondaryClosingList) && (
          <MuiGrid size={gridSize} className="form-field">
            <FormController
              control={control}
              name={ProgramClosureFormKey.secondaryClosingReason}
            >
              <MuiSelect
                id={`${pageId}-${ProgramClosureFormKey.secondaryClosingReason}`}
                label={getLabel(programSiteCoreKeys.secondaryClosingReason)}
                placeholder={commonLabels.selectPlaceHolder}
                options={secondaryClosingList}
                isMandatory
                visibleDefaultSelectOption
                requiredARIALabel={commonARIALabel.required}
              />
            </FormController>
          </MuiGrid>
        )}
      </MuiGrid>
      <MuiGrid container className="form-container change-status-form">
        <MuiGrid size={gridSize} className="form-field">
          <FormController
            control={control}
            name={ProgramClosureFormKey.closedDate}
          >
            <MuiTextField
              id={`${pageId}-${ProgramClosureFormKey.closedDate}`}
              label={getLabel(programSiteCoreKeys.closedDate)}
              disabled
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>
        <MuiGrid size={gridSize} className="form-field">
          <FormController
            control={control}
            name={ProgramClosureFormKey.closedBy}
          >
            <MuiTextField
              id={`${pageId}-${ProgramClosureFormKey.closedBy}`}
              label={getLabel(programSiteCoreKeys.closedBy)}
              disabled
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>
      </MuiGrid>
      {isVisibleLandingInformation && (
        <>
          {renderHeader(programSiteCoreKeys.landedInformationHeader)}
          <MuiTabs
            tabs={tabs}
            value={selectedTab}
            onChange={onTabChange}
            tabsTestIds={[tabIdGeneral, tabIdEmployer]}
          >
            <MuiTabPanel
              panelId={`${pageId}-panel1`}
              value={selectedTab}
              index={0}
              isPanelWithMount
              ariaLabelledById={tabIdGeneral}
            >
              <GeneralInformation
                pageId={pageId}
                userCountryDateFormat={userCountryDateFormat}
                countryList={setApiDataToDropDownOption(countryListData, true)}
                programStartDate={programStartDate}
                deliveryCountry={deliveryCountry}
              />
            </MuiTabPanel>
            <MuiTabPanel
              panelId={`${pageId}-panel2`}
              value={selectedTab}
              index={1}
              isPanelWithMount
              ariaLabelledById={tabIdEmployer}
            >
              <EmployeeInformation
                pageId={pageId}
                userCountryDateFormat={userCountryDateFormat}
                deliveryCountry={deliveryCountry}
              />
            </MuiTabPanel>
          </MuiTabs>
        </>
      )}
    </MuiBox>
  );
};
